import EastRoundedIcon from "@mui/icons-material/EastRounded"
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded"
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded"
import {
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Typography,
    useTheme,
} from "@mui/material"
import { ReactElement, useState } from "react"

import { GRID_SPACING } from "../../../themes/defaultThemeParams"
import AppButton from "../../buttons/appButton"
import AppDialog from "../../dialog/appDialog"
import { AppCardHeader } from "../../surfaces/appCard"
import DisplayIf from "../../wrappers/displayIf"

type IProcessOverviewTipsProps = {
    isOpen: boolean
    close: () => void
    popUpTitle: string
    popUpSubTitle: string
    processSteps?: Array<{
        label: string
        description: string
    }>
    tipsQAndA?: Array<{
        question: string
        answer: string
        points?: string[]
    }>
    actionButton?: boolean
    actionButtonLabel?: string
    action?: () => void
}

export const CX_PROCESS_STEPS = [
    {
        label: "Submit Commissioning Information",
        description: `You will need to verify the proper installation and operation of the power generating module by conducting tests to ensure safety and functionality in accordance with the design and EREC requirements. In the event of multiple device installations, some information would be required for each power generating module. This will be displayed with in the form.`,
    },
    {
        label: "We send pass the info to the DNO",
        description: "We will pass it over to the DNO for review",
    },
    {
        label: "DNO Reviews and Responds",
        description:
            "The DNO will review the information and either accept it or request an update. If they require an update, you will be notified via email and on here. You will be able to re-submit the information once you have made the necessary changes.",
    },
    {
        label: "DNO Accepts Your Information",
        description:
            "Once the DNO is satisfied, they will accept your information. The customer may also be notified via email (this varies by DNO and is at their discretion). The DNO may opt to provide an export MPAN at this stage - this is also at their discretion.",
    },
]

export const AMEND_PROCESS_STEPS = [
    {
        label: "Amendment Submitted",
        description: `You will need to update your application with the correct information, where your application will be re-assessed. Once your application has been approved, you will be able to submit your commissioning information.`,
    },
    {
        label: "Application Re-Assessed",
        description:
            "We will reassess your application and either (re) Auto-Approve or (re) escalate it to the DNO. If it is escalated, it will be sent to the DNO for (re) review. You will be unable to provide commissioning information until your application has been (re) approved. No emails or customer communications will be issued during the reassessment process.",
    },
    {
        label: "Once Approved, Commissioning can Proceed",
        description:
            "Once the revised application has been either (re) approved, commissioning information can be submitted.",
    },
]

export const CX_TIPS_Q_AND_A = [
    {
        question: "Can I get an export MPAN?",
        answer: "Yes, DNOs can provide an export MPAN when they accept your information. Whether to provide an export MPAN is at the discretion of the DNO.",
    },
    {
        question: "What attachments do I need to upload?",
        answer: "You will need to provide the following information (at least) to support the commissioning process:",
        points: [
            "A copy of the final circuit diagram",
            "Schedule of protection settings (This can be part of the final circuit diagram)",
            "Final single line diagram for Export Limiting Scheme (If changed from the application stage)",
        ],
    },
]

/**
 * A dialog that displays an overview of the process that the user will need to
 * follow to submit their commissioning information.
 */
const ProcessOverviewTips = ({
    isOpen,
    close,
    popUpTitle,
    popUpSubTitle,
    processSteps,
    tipsQAndA,
    actionButton = false,
    actionButtonLabel,
    action,
}: IProcessOverviewTipsProps): ReactElement => {
    const theme = useTheme()
    const [activeStep, setActiveStep] = useState(0)

    // stepper handlers
    const handleNext = (): void => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
    const handleBack = (): void => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1)
    }

    return (
        <AppDialog isOpen={isOpen}>
            <DialogTitle>
                <AppCardHeader
                    titleTypographyProps={{
                        variant: "h5",
                        fontWeight: "500",
                    }}
                    title={popUpTitle}
                    subheader={popUpSubTitle}
                />
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Stepper activeStep={activeStep} orientation="vertical">
                        {processSteps?.map((step, index) => (
                            <Step key={step.label}>
                                <StepLabel>{step.label}</StepLabel>
                                <StepContent>
                                    <Typography>{step.description}</Typography>
                                    <Box sx={{ mb: 2 }}>
                                        <div>
                                            <IconButton
                                                disabled={index === 0}
                                                onClick={handleBack}
                                                sx={{
                                                    mt: 1,
                                                    mr: 1,
                                                }}
                                            >
                                                <KeyboardArrowUpRoundedIcon fontSize="large" />
                                            </IconButton>

                                            <IconButton
                                                disabled={
                                                    index ===
                                                    processSteps.length - 1
                                                }
                                                onClick={handleNext}
                                                sx={{
                                                    mt: 1,
                                                    mr: 1,
                                                }}
                                            >
                                                <KeyboardArrowDownRoundedIcon fontSize="large" />
                                            </IconButton>
                                        </div>
                                    </Box>
                                </StepContent>
                            </Step>
                        ))}
                    </Stepper>

                    <DisplayIf condition={tipsQAndA != null}>
                        <Stack
                            direction="column"
                            spacing={GRID_SPACING}
                            divider={<Divider />}
                        >
                            {tipsQAndA?.map((tip) => (
                                <Box
                                    key={tip.question}
                                    borderLeft={3}
                                    color={theme.palette.primary.main}
                                    paddingLeft={2}
                                >
                                    <Typography
                                        variant="body1"
                                        fontWeight={600}
                                        color="text.primary"
                                    >
                                        {tip.question}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        component="span"
                                    >
                                        {Array.isArray(tip.points) ? (
                                            <ul>
                                                {tip.points.map((point) => (
                                                    <li key={point}>{point}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            tip.answer
                                        )}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </DisplayIf>
                </Stack>
            </DialogContent>
            <DialogActions>
                <AppButton variant="outlined" color="primary" onClick={close}>
                    Close
                </AppButton>
                <DisplayIf condition={actionButton}>
                    <AppButton
                        variant="contained"
                        color="primary"
                        onClick={action}
                        endIcon={<EastRoundedIcon />}
                    >
                        {actionButtonLabel}
                    </AppButton>
                </DisplayIf>
            </DialogActions>
        </AppDialog>
    )
}

ProcessOverviewTips.defaultProps = {
    processSteps: null,
    tipsQAndA: null,
    actionButton: false,
    actionButtonLabel: "",
    action: null,
}

export default ProcessOverviewTips
